export type ImagePathOptions = {
  routeMode?: 'density' | 'strokeTrace';
  maxPoints?: number;
  maxSize?: number;
  thresholdLow?: number;
  thresholdHigh?: number;
  thinEdges?: boolean;
  useCenterline?: boolean;
  inkThreshold?: number;
};

export type ImagePathResult = {
  path: number[][];
  width: number;
  height: number;
  modeUsed?: 'density' | 'strokeTrace';
  metrics?: unknown;
};

export type ImageWorkerProgress =
  | { progress?: string; message?: string; elapsedMs?: number }
  | { previewPath?: number[][]; width?: number; height?: number; elapsedMs?: number }
  | string;

export type ApplyPathWorkerResult = {
  appliedPoints: Array<{ lat: number; lng: number; timestamp?: string | number }>;
  fitArrayBuffer: ArrayBuffer;
};

export type ApplyPathInput = {
  imagePathResult: { path: number[][]; width: number; height: number };
  routeArrayBuffer: ArrayBuffer;
  sensorArrayBuffer?: ArrayBuffer;
  center?: { lat: number; lng: number };
};

import {
  TRACK_REMAP_FIT_WORKER_URL,
  TRACK_REMAP_IMAGE_WORKER_URL,
} from './trackRemapVendor';

export type WorkerUrls = {
  imageWorkerUrl?: string;
  fitWorkerUrl?: string;
};

const DEFAULT_IMAGE_WORKER_URL = TRACK_REMAP_IMAGE_WORKER_URL;
const DEFAULT_FIT_WORKER_URL = TRACK_REMAP_FIT_WORKER_URL;

export async function runImageToPathWorker(
  imageArrayBuffer: ArrayBuffer,
  options: ImagePathOptions,
  onProgress: (progress: ImageWorkerProgress) => void,
  urls: WorkerUrls = {},
  jobKey: string,
): Promise<ImagePathResult> {
  const workerUrl = urls.imageWorkerUrl ?? DEFAULT_IMAGE_WORKER_URL;
  const worker = new Worker(workerUrl);

  return await new Promise((resolve, reject) => {
    const cleanup = () => {
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onError);
      worker.terminate();
    };

    const onError = (ev: ErrorEvent) => {
      cleanup();
      reject(new Error(`Image worker failed: ${ev.message || 'unknown error'}`));
    };

    const onMessage = (ev: MessageEvent<any>) => {
      const d = ev.data;
      if (d?.jobKey && d.jobKey !== jobKey) return;

      if (d?.error) {
        cleanup();
        reject(new Error(String(d.error)));
        return;
      }

      if (d?.path && d?.width != null && d?.height != null) {
        cleanup();
        resolve({
          path: d.path,
          width: d.width,
          height: d.height,
          modeUsed: d.modeUsed,
          metrics: d.metrics,
        });
        return;
      }

      onProgress(d);
    };

    worker.addEventListener('message', onMessage);
    worker.addEventListener('error', onError);

    const payload = { imageArrayBuffer, options, jobKey };
    worker.postMessage(payload, [imageArrayBuffer]);
  });
}

export async function runApplyPathToFitWorker(
  input: ApplyPathInput,
  urls: WorkerUrls = {},
  jobKey: string,
): Promise<ApplyPathWorkerResult> {
  const workerUrl = urls.fitWorkerUrl ?? DEFAULT_FIT_WORKER_URL;
  const worker = new Worker(workerUrl);

  return await new Promise((resolve, reject) => {
    const cleanup = () => {
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onError);
      worker.terminate();
    };

    const onError = (ev: ErrorEvent) => {
      cleanup();
      reject(new Error(`FIT worker failed: ${ev.message || 'unknown error'}`));
    };

    const onMessage = (ev: MessageEvent<any>) => {
      const d = ev.data;
      if (d?.jobKey && d.jobKey !== jobKey) return;

      if (d?.error) {
        cleanup();
        reject(new Error(String(d.error)));
        return;
      }

      if (d?.appliedPoints && d?.fitArrayBuffer) {
        cleanup();
        resolve({ appliedPoints: d.appliedPoints, fitArrayBuffer: d.fitArrayBuffer });
        return;
      }

      // 想定外メッセージ
      cleanup();
      reject(new Error('Unexpected message from FIT worker'));
    };

    worker.addEventListener('message', onMessage);
    worker.addEventListener('error', onError);

    const transfer: ArrayBuffer[] = [input.routeArrayBuffer];
    if (input.sensorArrayBuffer) transfer.push(input.sensorArrayBuffer);

    worker.postMessage(
      {
        imagePathResult: input.imagePathResult,
        routeArrayBuffer: input.routeArrayBuffer,
        sensorArrayBuffer: input.sensorArrayBuffer,
        center: input.center,
        jobKey,
      },
      transfer,
    );
  });
}

